/**
 * Comprehensive Inventory Management Hook
 *
 * Provides all inventory operations with proper error handling, caching, and real-time updates
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useToast } from '@/contexts/ToastContext';
import { PaginationMeta } from '@/types/datatable';
import {
  ABCAnalysisConfig,
  ABCAnalysisResult,
  BulkInventoryOperation,
  BulkOperationResult,
  DemandForecast,
  InventoryAnalyticsDashboard,
  InventoryFormData,
  InventoryItem,
  InventoryMetrics,
  InventorySearchConfig,
  StockAlert,
  StockReservation,
  StockUpdate,
  ValuationReport,
} from '@/types/inventory';

import { useEnhancedApi } from './use-enhanced-api';

// Hook return type interface
interface UseInventoryReturn {
  // Data state
  items: InventoryItem[];
  metrics: InventoryMetrics | null;
  analytics: InventoryAnalyticsDashboard | null;
  stockAlerts: StockAlert[];
  categories: string[];

  // Loading states
  loading: boolean;
  loadingMetrics: boolean;
  loadingAnalytics: boolean;
  loadingAlerts: boolean;

  // Pagination
  pagination: PaginationMeta | undefined;

  // Basic CRUD operations
  fetchItems: (config?: InventorySearchConfig) => Promise<void>;
  fetchItem: (id: string) => Promise<InventoryItem | null>;
  createItem: (data: InventoryFormData) => Promise<boolean>;
  updateItem: (
    id: string,
    data: Partial<InventoryFormData>
  ) => Promise<boolean>;
  deleteItem: (id: string) => Promise<boolean>;

  // Bulk operations
  bulkCreate: (items: InventoryFormData[]) => Promise<BulkOperationResult>;
  bulkUpdate: (
    operation: BulkInventoryOperation
  ) => Promise<BulkOperationResult>;
  bulkDelete: (inventoryIds: string[]) => Promise<boolean>;
  bulkApprove: (inventoryIds: string[]) => Promise<boolean>;

  // Stock management
  updateStock: (id: string, update: StockUpdate) => Promise<boolean>;
  reserveStock: (reservation: StockReservation) => Promise<boolean>;
  releaseStock: (reservation: Partial<StockReservation>) => Promise<boolean>;

  // Approval workflow
  approveItem: (id: string) => Promise<boolean>;
  rejectItem: (id: string) => Promise<boolean>;

  // Analytics functions
  fetchMetrics: () => Promise<void>;
  fetchAnalyticsDashboard: (params?: Record<string, any>) => Promise<void>;
  getABCAnalysis: (
    config: ABCAnalysisConfig
  ) => Promise<ABCAnalysisResult | null>;
  getValuationReport: (config: any) => Promise<ValuationReport | null>;
  getDemandForecast: (
    params?: Record<string, any>
  ) => Promise<DemandForecast | null>;

  // Alerts and monitoring
  fetchStockAlerts: (params?: Record<string, any>) => Promise<void>;

  // Variant management
  addVariant: (inventoryId: string, variant: any) => Promise<boolean>;
  updateVariant: (
    inventoryId: string,
    variantId: string,
    data: any
  ) => Promise<boolean>;
  deleteVariant: (inventoryId: string, variantId: string) => Promise<boolean>;

  // Batch management
  addBatch: (inventoryId: string, batch: any) => Promise<boolean>;
  updateBatch: (
    inventoryId: string,
    batchId: string,
    data: any
  ) => Promise<boolean>;

  // Quality management
  updateQuality: (inventoryId: string, quality: any) => Promise<boolean>;
  addCertification: (
    inventoryId: string,
    certification: any
  ) => Promise<boolean>;

  // Supplier management
  addSupplier: (inventoryId: string, supplier: any) => Promise<boolean>;
  updateSupplierInfo: (
    inventoryId: string,
    supplierId: string,
    data: any
  ) => Promise<boolean>;
  removeSupplier: (inventoryId: string, supplierId: string) => Promise<boolean>;

  // Media management
  uploadImage: (inventoryId: string, file: File) => Promise<boolean>;
  uploadDocument: (inventoryId: string, file: File) => Promise<boolean>;
  deleteMedia: (
    inventoryId: string,
    mediaId: string,
    type: 'image' | 'document'
  ) => Promise<boolean>;

  // Export functionality
  exportData: (config?: any) => Promise<void>;

  // Categories
  fetchCategories: () => Promise<void>;
  createCategory: (category: any) => Promise<boolean>;

  // Utility functions
  refreshData: () => Promise<void>;
  clearCache: () => void;
}

export const useInventory = (): UseInventoryReturn => {
  const api = useEnhancedApi();
  const { showSuccess, showError } = useToast();

  // State management
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [metrics, setMetrics] = useState<InventoryMetrics | null>(null);
  const [analytics, setAnalytics] =
    useState<InventoryAnalyticsDashboard | null>(null);
  const [stockAlerts, setStockAlerts] = useState<StockAlert[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState<PaginationMeta | undefined>(
    undefined
  );

  // Current search config for refresh
  const [currentSearchConfig, setCurrentSearchConfig] = useState<
    InventorySearchConfig | undefined
  >();

  // Basic CRUD Operations
  const fetchItems = useCallback(
    async (config?: InventorySearchConfig) => {
      setLoading(true);
      try {
        setCurrentSearchConfig(config);

        let response;
        if (config && Object.keys(config).length > 0) {
          // Use advanced search
          response = await api.inventory.search(config);
        } else {
          // Use basic fetch
          response = await api.inventory.getAll(config);
        }

        if (response.success && response.data) {
          const data = response.data;

          // Handle both new and legacy response formats
          if (Array.isArray(data.inventory)) {
            setItems(data.inventory);
            if (data.pagination) {
              setPagination({
                page: data.pagination.page,
                limit: data.pagination.limit,
                total: data.pagination.totalCount,
                totalPages: data.pagination.totalPages,
                hasNext: data.pagination.hasNextPage,
                hasPrev: data.pagination.hasPrevPage,
              });
            }
          } else if (data.inventory?.docs) {
            // Legacy format
            setItems(data.inventory.docs);
            setPagination({
              page: data.inventory.page,
              limit: data.inventory.limit,
              total: data.inventory.totalDocs,
              totalPages: data.inventory.totalPages,
              hasNext: data.inventory.hasNextPage,
              hasPrev: data.inventory.hasPrevPage,
            });
          } else {
            setItems([]);
            setPagination(undefined);
          }
        }
      } catch (error) {
        showError('Failed to fetch inventory items');
        setItems([]);
        setPagination(undefined);
      } finally {
        setLoading(false);
      }
    },
    [api.inventory, showError]
  );

  const fetchItem = useCallback(
    async (id: string): Promise<InventoryItem | null> => {
      try {
        const response = await api.inventory.getById(id);
        if (response.success && response.data) {
          // Handle both new and legacy response formats
          const item = response.data.inventory || response.data;
          return item;
        }
        return null;
      } catch (error) {
        showError('Failed to fetch inventory item');
        return null;
      }
    },
    [api.inventory, showError]
  );

  const createItem = useCallback(
    async (data: InventoryFormData): Promise<boolean> => {
      try {
        const response = await api.inventory.create(data);
        if (response.success) {
          showSuccess(
            response.message || 'Inventory item created successfully'
          );
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Failed to create inventory item');
          return false;
        }
      } catch (error) {
        showError('Failed to create inventory item');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const updateItem = useCallback(
    async (id: string, data: Partial<InventoryFormData>): Promise<boolean> => {
      try {
        const response = await api.inventory.update(id, data);
        if (response.success) {
          showSuccess(
            response.message || 'Inventory item updated successfully'
          );
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Failed to update inventory item');
          return false;
        }
      } catch (error) {
        showError('Failed to update inventory item');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const deleteItem = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const response = await api.inventory.delete(id);
        if (response.success) {
          showSuccess(
            response.message || 'Inventory item deleted successfully'
          );
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Failed to delete inventory item');
          return false;
        }
      } catch (error) {
        showError('Failed to delete inventory item');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Bulk Operations
  const bulkCreate = useCallback(
    async (items: InventoryFormData[]): Promise<BulkOperationResult> => {
      try {
        const response = await api.inventory.bulkCreate({
          inventoryItems: items,
        });
        if (response.success) {
          showSuccess(
            `Successfully created ${response.data?.successful?.length || 0} items`
          );
          await refreshData();
          return response.data;
        } else {
          showError(response.message || 'Bulk create failed');
          return {
            successful: [],
            failed: [],
            summary: { total: 0, successful: 0, failed: items.length },
          };
        }
      } catch (error) {
        showError('Bulk create operation failed');
        return {
          successful: [],
          failed: [],
          summary: { total: 0, successful: 0, failed: items.length },
        };
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const bulkUpdate = useCallback(
    async (operation: BulkInventoryOperation): Promise<BulkOperationResult> => {
      try {
        const response = await api.inventory.bulkUpdate(operation);
        if (response.success) {
          showSuccess(
            `Successfully updated ${response.data?.successful?.length || 0} items`
          );
          await refreshData();
          return response.data;
        } else {
          showError(response.message || 'Bulk update failed');
          return {
            successful: [],
            failed: [],
            summary: {
              total: 0,
              successful: 0,
              failed: operation.inventoryIds.length,
            },
          };
        }
      } catch (error) {
        showError('Bulk update operation failed');
        return {
          successful: [],
          failed: [],
          summary: {
            total: 0,
            successful: 0,
            failed: operation.inventoryIds.length,
          },
        };
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const bulkDelete = useCallback(
    async (inventoryIds: string[]): Promise<boolean> => {
      try {
        const response = await api.inventory.bulkDelete(inventoryIds);
        if (response.success) {
          showSuccess(`Successfully deleted ${inventoryIds.length} items`);
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Bulk delete failed');
          return false;
        }
      } catch (error) {
        showError('Bulk delete operation failed');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const bulkApprove = useCallback(
    async (inventoryIds: string[]): Promise<boolean> => {
      try {
        const response = await api.inventory.bulkApprove(inventoryIds);
        if (response.success) {
          showSuccess(`Successfully approved ${inventoryIds.length} items`);
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Bulk approve failed');
          return false;
        }
      } catch (error) {
        showError('Bulk approve operation failed');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Stock Management
  const updateStock = useCallback(
    async (id: string, update: StockUpdate): Promise<boolean> => {
      try {
        const response = await api.inventory.updateStock(id, update);
        if (response.success) {
          showSuccess(response.message || 'Stock updated successfully');
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Failed to update stock');
          return false;
        }
      } catch (error) {
        showError('Failed to update stock');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const reserveStock = useCallback(
    async (reservation: StockReservation): Promise<boolean> => {
      try {
        const response = await api.inventory.reserveStock(reservation);
        if (response.success) {
          showSuccess(response.message || 'Stock reserved successfully');
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Failed to reserve stock');
          return false;
        }
      } catch (error) {
        showError('Failed to reserve stock');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const releaseStock = useCallback(
    async (reservation: Partial<StockReservation>): Promise<boolean> => {
      try {
        const response = await api.inventory.releaseStock(reservation);
        if (response.success) {
          showSuccess(response.message || 'Stock released successfully');
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Failed to release stock');
          return false;
        }
      } catch (error) {
        showError('Failed to release stock');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Approval Workflow
  const approveItem = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const response = await api.inventory.approve(id);
        if (response.success) {
          showSuccess(response.message || 'Item approved successfully');
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Failed to approve item');
          return false;
        }
      } catch (error) {
        showError('Failed to approve item');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const rejectItem = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const response = await api.inventory.reject(id);
        if (response.success) {
          showSuccess(response.message || 'Item rejected successfully');
          await refreshData();
          return true;
        } else {
          showError(response.message || 'Failed to reject item');
          return false;
        }
      } catch (error) {
        showError('Failed to reject item');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Analytics Functions
  const fetchMetrics = useCallback(async () => {
    setLoadingMetrics(true);
    try {
      const response = await api.inventory.getMetrics();
      if (response.success && response.data) {
        setMetrics(response.data);
      }
    } catch (error) {
      showError('Failed to fetch inventory metrics');
    } finally {
      setLoadingMetrics(false);
    }
  }, [api.inventory, showError]);

  const fetchAnalyticsDashboard = useCallback(
    async (params?: Record<string, any>) => {
      setLoadingAnalytics(true);
      try {
        const response = await api.inventory.getAnalyticsDashboard(params);
        if (response.success && response.data) {
          setAnalytics(response.data);
        }
      } catch (error) {
        showError('Failed to fetch analytics dashboard');
      } finally {
        setLoadingAnalytics(false);
      }
    },
    [api.inventory, showError]
  );

  const getABCAnalysis = useCallback(
    async (config: ABCAnalysisConfig): Promise<ABCAnalysisResult | null> => {
      try {
        const response = await api.inventory.getABCAnalysis(config);
        if (response.success && response.data) {
          return response.data;
        }
        return null;
      } catch (error) {
        showError('Failed to get ABC analysis');
        return null;
      }
    },
    [api.inventory, showError]
  );

  const getValuationReport = useCallback(
    async (config: any): Promise<ValuationReport | null> => {
      try {
        const response = await api.inventory.getValuationReport(config);
        if (response.success && response.data) {
          return response.data;
        }
        return null;
      } catch (error) {
        showError('Failed to get valuation report');
        return null;
      }
    },
    [api.inventory, showError]
  );

  const getDemandForecast = useCallback(
    async (params?: Record<string, any>): Promise<DemandForecast | null> => {
      try {
        const response = await api.inventory.getDemandForecast(params);
        if (response.success && response.data) {
          return response.data;
        }
        return null;
      } catch (error) {
        showError('Failed to get demand forecast');
        return null;
      }
    },
    [api.inventory, showError]
  );

  // Alerts and Monitoring
  const fetchStockAlerts = useCallback(
    async (params?: Record<string, any>) => {
      setLoadingAlerts(true);
      try {
        const response = await api.inventory.getStockAlerts(params);
        if (response.success && response.data) {
          setStockAlerts(response.data.alerts || []);
        }
      } catch (error) {
        showError('Failed to fetch stock alerts');
      } finally {
        setLoadingAlerts(false);
      }
    },
    [api.inventory, showError]
  );

  // Variant Management
  const addVariant = useCallback(
    async (inventoryId: string, variant: any): Promise<boolean> => {
      try {
        const response = await api.inventory.addVariant(inventoryId, variant);
        if (response.success) {
          showSuccess(response.message || 'Variant added successfully');
          return true;
        } else {
          showError(response.message || 'Failed to add variant');
          return false;
        }
      } catch (error) {
        showError('Failed to add variant');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const updateVariant = useCallback(
    async (
      inventoryId: string,
      variantId: string,
      data: any
    ): Promise<boolean> => {
      try {
        const response = await api.inventory.updateVariant(
          inventoryId,
          variantId,
          data
        );
        if (response.success) {
          showSuccess(response.message || 'Variant updated successfully');
          return true;
        } else {
          showError(response.message || 'Failed to update variant');
          return false;
        }
      } catch (error) {
        showError('Failed to update variant');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const deleteVariant = useCallback(
    async (inventoryId: string, variantId: string): Promise<boolean> => {
      try {
        const response = await api.inventory.deleteVariant(
          inventoryId,
          variantId
        );
        if (response.success) {
          showSuccess(response.message || 'Variant deleted successfully');
          return true;
        } else {
          showError(response.message || 'Failed to delete variant');
          return false;
        }
      } catch (error) {
        showError('Failed to delete variant');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Batch Management
  const addBatch = useCallback(
    async (inventoryId: string, batch: any): Promise<boolean> => {
      try {
        const response = await (api.inventory as any).addBatch(
          inventoryId,
          batch
        );
        if (response.success) {
          showSuccess(response.message || 'Batch added successfully');
          return true;
        } else {
          showError(response.message || 'Failed to add batch');
          return false;
        }
      } catch (error) {
        showError('Failed to add batch');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const updateBatch = useCallback(
    async (
      inventoryId: string,
      batchId: string,
      data: any
    ): Promise<boolean> => {
      try {
        const response = await api.inventory.updateBatch(
          inventoryId,
          batchId,
          data
        );
        if (response.success) {
          showSuccess(response.message || 'Batch updated successfully');
          return true;
        } else {
          showError(response.message || 'Failed to update batch');
          return false;
        }
      } catch (error) {
        showError('Failed to update batch');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Quality Management
  const updateQuality = useCallback(
    async (inventoryId: string, quality: any): Promise<boolean> => {
      try {
        const response = await api.inventory.updateQualityInfo(
          inventoryId,
          quality
        );
        if (response.success) {
          showSuccess(
            response.message || 'Quality information updated successfully'
          );
          return true;
        } else {
          showError(response.message || 'Failed to update quality information');
          return false;
        }
      } catch (error) {
        showError('Failed to update quality information');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const addCertification = useCallback(
    async (inventoryId: string, certification: any): Promise<boolean> => {
      try {
        const response = await api.inventory.addCertification(
          inventoryId,
          certification
        );
        if (response.success) {
          showSuccess(response.message || 'Certification added successfully');
          return true;
        } else {
          showError(response.message || 'Failed to add certification');
          return false;
        }
      } catch (error) {
        showError('Failed to add certification');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Supplier Management
  const addSupplier = useCallback(
    async (inventoryId: string, supplier: any): Promise<boolean> => {
      try {
        const response = await api.inventory.addSupplier(inventoryId, supplier);
        if (response.success) {
          showSuccess(response.message || 'Supplier added successfully');
          return true;
        } else {
          showError(response.message || 'Failed to add supplier');
          return false;
        }
      } catch (error) {
        showError('Failed to add supplier');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const updateSupplierInfo = useCallback(
    async (
      inventoryId: string,
      supplierId: string,
      data: any
    ): Promise<boolean> => {
      try {
        const response = await api.inventory.updateSupplierInfo(
          inventoryId,
          supplierId,
          data
        );
        if (response.success) {
          showSuccess(
            response.message || 'Supplier information updated successfully'
          );
          return true;
        } else {
          showError(
            response.message || 'Failed to update supplier information'
          );
          return false;
        }
      } catch (error) {
        showError('Failed to update supplier information');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const removeSupplier = useCallback(
    async (inventoryId: string, supplierId: string): Promise<boolean> => {
      try {
        const response = await api.inventory.removeSupplier(
          inventoryId,
          supplierId
        );
        if (response.success) {
          showSuccess(response.message || 'Supplier removed successfully');
          return true;
        } else {
          showError(response.message || 'Failed to remove supplier');
          return false;
        }
      } catch (error) {
        showError('Failed to remove supplier');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Media Management
  const uploadImage = useCallback(
    async (inventoryId: string, file: File): Promise<boolean> => {
      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await api.inventory.uploadImage(inventoryId, formData);
        if (response.success) {
          showSuccess(response.message || 'Image uploaded successfully');
          return true;
        } else {
          showError(response.message || 'Failed to upload image');
          return false;
        }
      } catch (error) {
        showError('Failed to upload image');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const uploadDocument = useCallback(
    async (inventoryId: string, file: File): Promise<boolean> => {
      try {
        const formData = new FormData();
        formData.append('document', file);

        const response = await api.inventory.uploadDocument(
          inventoryId,
          formData
        );
        if (response.success) {
          showSuccess(response.message || 'Document uploaded successfully');
          return true;
        } else {
          showError(response.message || 'Failed to upload document');
          return false;
        }
      } catch (error) {
        showError('Failed to upload document');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  const deleteMedia = useCallback(
    async (
      inventoryId: string,
      mediaId: string,
      type: 'image' | 'document'
    ): Promise<boolean> => {
      try {
        const response = await api.inventory.deleteMedia(
          inventoryId,
          mediaId,
          type
        );
        if (response.success) {
          showSuccess(response.message || 'Media deleted successfully');
          return true;
        } else {
          showError(response.message || 'Failed to delete media');
          return false;
        }
      } catch (error) {
        showError('Failed to delete media');
        return false;
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Export Functionality
  const exportData = useCallback(
    async (config?: any) => {
      try {
        const response = await api.inventory.export(config);
        if (response.success) {
          // Handle file download
          if (response.data?.downloadUrl) {
            const link = document.createElement('a');
            link.href = response.data.downloadUrl;
            link.download = `inventory-export-${new Date().toISOString().split('T')[0]}.${config?.format || 'csv'}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          showSuccess(response.message || 'Export completed successfully');
        } else {
          showError(response.message || 'Export failed');
        }
      } catch (error) {
        showError('Export operation failed');
      }
    },
    [api.inventory, showSuccess, showError]
  );

  // Categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.inventory.getCategories();
      if (response.success && response.data) {
        // API returns { data: { categories: string[], correlationId } }
        const list = Array.isArray(response.data)
          ? (response.data as unknown as string[])
          : (response.data as any).categories || [];
        setCategories(list);
      }
    } catch (error) {
      showError('Failed to fetch categories');
    }
  }, [api.inventory, showError]);

  const createCategory = useCallback(
    async (category: any): Promise<boolean> => {
      try {
        const response = await api.inventory.createCategory(category);
        if (response.success) {
          showSuccess(response.message || 'Category created successfully');
          await fetchCategories();
          return true;
        } else {
          showError(response.message || 'Failed to create category');
          return false;
        }
      } catch (error) {
        showError('Failed to create category');
        return false;
      }
    },
    [api.inventory, showSuccess, showError, fetchCategories]
  );

  // Utility Functions
  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchItems(currentSearchConfig),
      fetchMetrics(),
      fetchStockAlerts(),
    ]);
  }, [fetchItems, fetchMetrics, fetchStockAlerts, currentSearchConfig]);

  const clearCache = useCallback(() => {
    setItems([]);
    setMetrics(null);
    setAnalytics(null);
    setStockAlerts([]);
    setCategories([]);
    setPagination(undefined);
    setCurrentSearchConfig(undefined);
  }, []);

  // Initial data loading
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Return the hook interface
  return useMemo(
    () => ({
      // Data state
      items,
      metrics,
      analytics,
      stockAlerts,
      categories,

      // Loading states
      loading,
      loadingMetrics,
      loadingAnalytics,
      loadingAlerts,

      // Pagination
      pagination,

      // Basic CRUD operations
      fetchItems,
      fetchItem,
      createItem,
      updateItem,
      deleteItem,

      // Bulk operations
      bulkCreate,
      bulkUpdate,
      bulkDelete,
      bulkApprove,

      // Stock management
      updateStock,
      reserveStock,
      releaseStock,

      // Approval workflow
      approveItem,
      rejectItem,

      // Analytics functions
      fetchMetrics,
      fetchAnalyticsDashboard,
      getABCAnalysis,
      getValuationReport,
      getDemandForecast,

      // Alerts and monitoring
      fetchStockAlerts,

      // Variant management
      addVariant,
      updateVariant,
      deleteVariant,

      // Batch management
      addBatch,
      updateBatch,

      // Quality management
      updateQuality,
      addCertification,

      // Supplier management
      addSupplier,
      updateSupplierInfo,
      removeSupplier,

      // Media management
      uploadImage,
      uploadDocument,
      deleteMedia,

      // Export functionality
      exportData,

      // Categories
      fetchCategories,
      createCategory,

      // Utility functions
      refreshData,
      clearCache,
    }),
    [
      items,
      metrics,
      analytics,
      stockAlerts,
      categories,
      loading,
      loadingMetrics,
      loadingAnalytics,
      loadingAlerts,
      pagination,
      fetchItems,
      fetchItem,
      createItem,
      updateItem,
      deleteItem,
      bulkCreate,
      bulkUpdate,
      bulkDelete,
      bulkApprove,
      updateStock,
      reserveStock,
      releaseStock,
      approveItem,
      rejectItem,
      fetchMetrics,
      fetchAnalyticsDashboard,
      getABCAnalysis,
      getValuationReport,
      getDemandForecast,
      fetchStockAlerts,
      addVariant,
      updateVariant,
      deleteVariant,
      addBatch,
      updateBatch,
      updateQuality,
      addCertification,
      addSupplier,
      updateSupplierInfo,
      removeSupplier,
      uploadImage,
      uploadDocument,
      deleteMedia,
      exportData,
      fetchCategories,
      createCategory,
      refreshData,
      clearCache,
    ]
  );
};

export default useInventory;
